"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import { Minus, Plus, AlertCircle } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { CheckoutSteps } from "@baluu/ui/components/checkout/checkout-steps";

type AddOn = {
  id: string;
  name: string;
  description?: string;
  price: number;
  maxQuantity?: number;
  minQuantity?: number;
  soldOut?: boolean;
  groupId?: string | null;
  sortOrder?: number;
};

type AddOnGroup = {
  id: string;
  name: string;
  description?: string | null;
  minSelection: number;
  maxSelection: number;
  addOns: AddOn[];
};

type AddOnSelection = {
  addOnId: string;
  quantity: number;
  groupId?: string | null;
  groupName?: string | null;
};

type AddOnsStepProps = {
  addOns: AddOn[];
  groups?: AddOnGroup[];
  selections: AddOnSelection[];
  onSelectionChange: (
    addOnId: string,
    quantity: number,
    groupId?: string | null,
    groupName?: string | null,
  ) => void;
  currency?: string;
  className?: string;
};

function formatPrice(price: number, currency: string = "GBP"): string {
  const symbols: Record<string, string> = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  const symbol = symbols[currency] || currency;
  return `${symbol}${(price / 100).toFixed(2)}`;
}

function AddOnsStep({
  addOns,
  groups = [],
  selections,
  onSelectionChange,
  currency = "GBP",
  className,
}: AddOnsStepProps) {
  const getQuantity = (addOnId: string): number => {
    const selection = selections.find((s) => s.addOnId === addOnId);
    return selection?.quantity ?? 0;
  };

  const getAllAddOns = (): AddOn[] => [
    ...addOns,
    ...groups.flatMap((g) => g.addOns),
  ];

  const getTotalAddOnsPrice = (): number => {
    const all = getAllAddOns();
    return selections.reduce((sum, selection) => {
      const addOn = all.find((a) => a.id === selection.addOnId);
      return sum + (addOn?.price ?? 0) * selection.quantity;
    }, 0);
  };

  const getTotalSelectedAddOns = (): number => {
    return selections.reduce((sum, s) => sum + s.quantity, 0);
  };

  const getGroupSelectionCount = (groupId: string): number => {
    return selections
      .filter((s) => s.groupId === groupId && s.quantity > 0)
      .reduce((sum, s) => sum + s.quantity, 0);
  };

  const isGroupValid = (group: AddOnGroup): boolean => {
    const count = getGroupSelectionCount(group.id);
    if (group.minSelection > 0 && count < group.minSelection) return false;
    if (group.maxSelection > 0 && count > group.maxSelection) return false;
    return true;
  };

  const hasStandalone = addOns.length > 0;
  const hasGroups = groups.length > 0;

  return (
    <div className={cn("space-y-6", className)}>
      <CheckoutSteps.Header
        title="Additional Services"
        description="Select any additional services you'd like to add to your booking."
      />

      {/* Standalone add-ons */}
      {hasStandalone && (
        <div className="space-y-3">
          {hasGroups && (
            <p className="text-sm font-medium text-foreground uppercase tracking-wide">
              Available Add-ons
            </p>
          )}

          <div className="space-y-3">
            {addOns.map((addOn) => (
              <AddOnSelector
                key={addOn.id}
                addOn={addOn}
                quantity={getQuantity(addOn.id)}
                currency={currency}
                onChange={(quantity) => onSelectionChange(addOn.id, quantity)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Grouped add-ons */}
      {groups.map((group) => {
        const selectionCount = getGroupSelectionCount(group.id);
        const valid = isGroupValid(group);
        const isRadioMode = group.maxSelection === 1;
        const isOptional = group.minSelection === 0;

        return (
          <div key={group.id} className="space-y-3">
            <div>
              <p className="text-sm font-medium text-foreground uppercase tracking-wide">
                {group.name}
                {group.minSelection > 0 && (
                  <span className="text-destructive ml-1">*</span>
                )}
              </p>
              {group.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {group.description}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {group.minSelection === 1 && group.maxSelection === 1 &&
                  "Select one option"}
                {group.minSelection === 0 && group.maxSelection === 1 &&
                  "Select one option (optional)"}
                {group.maxSelection > 1 &&
                  group.minSelection > 0 &&
                  `Select ${group.minSelection} to ${group.maxSelection}`}
                {group.maxSelection > 1 &&
                  group.minSelection === 0 &&
                  `Select up to ${group.maxSelection} (optional)`}
                {group.maxSelection === 0 &&
                  group.minSelection > 0 &&
                  `Select at least ${group.minSelection}`}
                {group.maxSelection === 0 &&
                  group.minSelection === 0 &&
                  "Select any (optional)"}
              </p>
            </div>

            {isRadioMode ? (
              <AddOnRadioGroup
                group={group}
                selections={selections}
                currency={currency}
                isOptional={isOptional}
                onSelectionChange={onSelectionChange}
              />
            ) : (
              <div className="space-y-3">
                {group.addOns.map((addOn) => (
                  <AddOnSelector
                    key={addOn.id}
                    addOn={addOn}
                    quantity={getQuantity(addOn.id)}
                    currency={currency}
                    onChange={(quantity) =>
                      onSelectionChange(
                        addOn.id,
                        quantity,
                        group.id,
                        group.name,
                      )
                    }
                    maxGroupQuantity={
                      group.maxSelection > 0
                        ? group.maxSelection - selectionCount + getQuantity(addOn.id)
                        : undefined
                    }
                  />
                ))}
              </div>
            )}

            {!valid && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4" />
                <span>
                  Please select{" "}
                  {group.minSelection === 1
                    ? "an option"
                    : `at least ${group.minSelection} options`}
                  {selectionCount > 0 && ` (${selectionCount} selected)`}
                </span>
              </div>
            )}
          </div>
        );
      })}

      {getTotalSelectedAddOns() > 0 && (
        <div className="p-4 bg-background rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">
              {getTotalSelectedAddOns()} add-on
              {getTotalSelectedAddOns() !== 1 ? "s" : ""} selected
            </span>
            <span className="font-semibold text-foreground">
              {formatPrice(getTotalAddOnsPrice(), currency)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

type AddOnSelectorProps = {
  addOn: AddOn;
  quantity: number;
  currency: string;
  onChange: (quantity: number) => void;
  maxGroupQuantity?: number;
};

function AddOnSelector({
  addOn,
  quantity,
  currency,
  onChange,
  maxGroupQuantity,
}: AddOnSelectorProps) {
  const minQuantity = addOn.minQuantity ?? 1;
  const maxQuantity = Math.min(
    addOn.maxQuantity ?? 99,
    maxGroupQuantity ?? 99,
  );
  // Always allow deselecting (going to 0), but once selected enforce minQuantity
  const canDecrease = quantity > 0 && !addOn.soldOut;
  const canIncrease = quantity < maxQuantity && !addOn.soldOut;

  return (
    <div
      className={cn(
        "flex items-center justify-between p-4 bg-card rounded-lg border border-border",
        addOn.soldOut && "opacity-50",
      )}
    >
      <div className="flex-1 min-w-0 mr-4">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-foreground">{addOn.name}</h4>
          {addOn.soldOut && (
            <span className="text-xs font-medium text-destructive bg-destructive/10-muted px-2 py-0.5 rounded">
              Sold out
            </span>
          )}
        </div>
        {addOn.description && (
          <p className="text-sm text-muted-foreground mt-1">
            {addOn.description}
          </p>
        )}
        {!addOn.soldOut && (
          <p className="text-sm font-semibold text-foreground mt-1">
            {formatPrice(addOn.price, currency)}
          </p>
        )}
      </div>

      {!addOn.soldOut && (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => {
              if (!canDecrease) return;
              // If decreasing would go below minQuantity, deselect entirely
              const next = quantity - 1;
              onChange(next < minQuantity ? 0 : next);
            }}
            disabled={!canDecrease}
            className="h-8 w-8 rounded-full"
          >
            <Minus className="w-4 h-4" />
          </Button>

          <span className="w-8 text-center font-medium text-foreground">
            {quantity}
          </span>

          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => {
              if (!canIncrease) return;
              // If currently at 0, jump to minQuantity
              onChange(quantity === 0 ? Math.max(minQuantity, 1) : quantity + 1);
            }}
            disabled={!canIncrease}
            className="h-8 w-8 rounded-full"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

type AddOnRadioGroupProps = {
  group: AddOnGroup;
  selections: AddOnSelection[];
  currency: string;
  isOptional: boolean;
  onSelectionChange: (
    addOnId: string,
    quantity: number,
    groupId?: string | null,
    groupName?: string | null,
  ) => void;
};

function AddOnRadioGroup({
  group,
  selections,
  currency,
  isOptional,
  onSelectionChange,
}: AddOnRadioGroupProps) {
  const selectedId = selections.find(
    (s) => s.groupId === group.id && s.quantity > 0,
  )?.addOnId;

  const handleSelect = (addOnId: string) => {
    // Deselect previous selection in this group
    if (selectedId && selectedId !== addOnId) {
      onSelectionChange(selectedId, 0, group.id, group.name);
    }

    if (selectedId === addOnId) {
      // Clicking the already-selected option: deselect if optional
      if (isOptional) {
        onSelectionChange(addOnId, 0, group.id, group.name);
      }
    } else {
      onSelectionChange(addOnId, 1, group.id, group.name);
    }
  };

  // Use dropdown if more than 5 items
  if (group.addOns.length > 5) {
    return (
      <select
        className="w-full p-3 bg-card border border-border rounded-lg text-foreground"
        value={selectedId || ""}
        onChange={(e) => {
          const value = e.target.value;
          // Deselect previous
          if (selectedId) {
            onSelectionChange(selectedId, 0, group.id, group.name);
          }
          if (value) {
            onSelectionChange(value, 1, group.id, group.name);
          }
        }}
      >
        {isOptional && <option value="">None</option>}
        {!isOptional && !selectedId && (
          <option value="" disabled>
            Select an option...
          </option>
        )}
        {group.addOns.map((addOn) => (
          <option key={addOn.id} value={addOn.id} disabled={addOn.soldOut}>
            {addOn.name} — {formatPrice(addOn.price, currency)}
            {addOn.soldOut ? " (Sold out)" : ""}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="space-y-2">
      {isOptional && (
        <button
          type="button"
          onClick={() => {
            if (selectedId) {
              onSelectionChange(selectedId, 0, group.id, group.name);
            }
          }}
          className={cn(
            "flex items-center w-full p-3 bg-card rounded-lg border text-left transition-colors",
            !selectedId
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50",
          )}
        >
          <div
            className={cn(
              "w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0",
              !selectedId ? "border-primary bg-primary" : "border-muted-foreground",
            )}
          >
            {!selectedId && (
              <div className="w-full h-full rounded-full flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-white" />
              </div>
            )}
          </div>
          <span className="text-sm text-muted-foreground">None</span>
        </button>
      )}
      {group.addOns.map((addOn) => {
        const isSelected = selectedId === addOn.id;
        return (
          <button
            type="button"
            key={addOn.id}
            onClick={() => !addOn.soldOut && handleSelect(addOn.id)}
            disabled={addOn.soldOut}
            className={cn(
              "flex items-center w-full p-3 bg-card rounded-lg border text-left transition-colors",
              isSelected
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50",
              addOn.soldOut && "opacity-50 cursor-not-allowed",
            )}
          >
            <div
              className={cn(
                "w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0",
                isSelected
                  ? "border-primary bg-primary"
                  : "border-muted-foreground",
              )}
            >
              {isSelected && (
                <div className="w-full h-full rounded-full flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground text-sm">
                  {addOn.name}
                </span>
                {addOn.soldOut && (
                  <span className="text-xs font-medium text-destructive">
                    Sold out
                  </span>
                )}
              </div>
              {addOn.description && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {addOn.description}
                </p>
              )}
            </div>
            {!addOn.soldOut && (
              <span className="text-sm font-semibold text-foreground ml-2">
                {formatPrice(addOn.price, currency)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export { AddOnsStep, AddOnRadioGroup };
export type { AddOn, AddOnGroup, AddOnSelection, AddOnsStepProps };
